import React, { useEffect, useState } from 'react';
import { Col, Input, List, Row, Select, Skeleton } from 'antd';
import { CloseOutlined, DownOutlined, ExclamationCircleFilled, SearchOutlined, UpOutlined } from '@ant-design/icons';
import './SupplierProducts.scss';
import { QuantumVendorDetails } from 'models/Supplier';
import CompareProductsTableModal from './CompareProductsTableModal';
import { useRecoilState } from 'recoil';
import { selectedSupplierDynamicsVendorState } from 'recoil/atoms';
import { SupplierProductAlternatives } from './SupplierProductAlternatives';
import { SupplierDynamicsVendor } from 'types/global';
import { FieldSorter } from 'shared/Helpers';

interface Props {
  quantumSupplier?: QuantumVendorDetails;
  setSelectedSupplierCardTab?: (value: ((prevState: string) => string) | string) => void;
  setViewInfo?: (value: { showPartnerInfo: boolean; showNPIInfo: boolean; showToggle: boolean }) => void;
  vendorsList?: Array<SupplierDynamicsVendor>;
}

type Product = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  features?: string[];
};

type CategoryItem = {
  product: Product;
};

export const SupplierProducts: React.FC<Props> = ({
  quantumSupplier,
  setSelectedSupplierCardTab,
  setViewInfo,
  vendorsList
}): React.ReactElement => {
  const [checkedStates, setCheckedStates] = useState<Record<number, Record<number, boolean>>>({});
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());
  const [categoriesData, setCategoriesData] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [_categories, setCategories] = useState([]);
  const [showCompareProductsModal, setShowCompareProductsModal] = useState<{
    open: boolean;
    comparisonProducts: any[] | undefined;
    unionOfFeatures: string[] | undefined;
  }>({
    open: false,
    comparisonProducts: undefined,
    unionOfFeatures: undefined
  });
  const [loadingProductsAndAlternatives, setloadingProductsAndAlternatives] = useState<boolean>(false);
  const [_, setSelectedVendor] = useRecoilState(selectedSupplierDynamicsVendorState);

  const toggleExpand = (index: number) => {
    const newExpandedIndices = new Set(expandedIndices);
    if (newExpandedIndices.has(index)) {
      newExpandedIndices.delete(index);
    } else {
      newExpandedIndices.add(index);
    }
    setExpandedIndices(newExpandedIndices);
  };
  const handleClearSelection = () => {
    setCheckedStates({});
  };
  // ----- BUYER CADDY / Quantum (Supplier Dynamics Plus Beta) - COMMENTED OUT -----
  const handleQuantumSearch = async () => {
    setloadingProductsAndAlternatives(true);
    if (quantumSupplier?.vendor_domain) {
      try {
        // const tempData = await fetchQuantumProductAlternatives(quantumSupplier?.vendor_domain);
        // setCategoriesData(tempData?.content);
        setCategoriesData(undefined);
      } catch (err) {
        console.error('Error occurred while fetching product and its alternatives', err);
      } finally {
        setloadingProductsAndAlternatives(false);
      }
    }
  };

  useEffect(() => {
    handleQuantumSearch();
    handleClearSelection();
  }, [quantumSupplier?.vendor_domain]);

  useEffect(() => {
    // const uniqueCategories: any = [...new Set(quantumSupplier?.content?.map((item) => item.category))].map(
    const uniqueCategories: any = [
      ...new Set(
        categoriesData?.map((item) => ({
          label: item?.product?.category,
          value: item?.product?.categoryId
        }))
      )
    ];
    setCategories(uniqueCategories);
  }, [categoriesData]);

  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value);
  };

  const quantumCategoryOptions: { label: string | undefined; value: string | undefined }[] = Array.isArray(
    categoriesData
  )
    ? [...new Set(categoriesData.map((item: CategoryItem) => item?.product?.category).filter(Boolean))]
        .map((cat) => ({
          label: cat,
          value: cat
        }))
        ?.sort((a, b) => FieldSorter(a?.label?.toLowerCase(), b?.label?.toLowerCase()))
    : [];

  const handleQuantumProductSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredQuantumProducts: CategoryItem[] = (Array.isArray(categoriesData) ? categoriesData : []).filter(
    (item: CategoryItem) => {
      const productName = item?.product?.name || '';
      const productDescription = item?.product?.description || '';
      const searchQuery = searchText.toLowerCase();

      const matchesSearch =
        productName.toLowerCase().includes(searchQuery) || productDescription.toLowerCase().includes(searchQuery);
      const matchesCategory = selectedCategory ? item?.product?.category === selectedCategory : true;

      return matchesSearch && matchesCategory;
    }
  );

  useEffect(() => {
    if (filteredQuantumProducts) {
      const filterProductIds = filteredQuantumProducts?.map((product) => product?.product?.id);
      if (Object.keys(checkedStates)?.length > 0 && !filterProductIds.includes(Object.keys(checkedStates)[0])) {
        setCheckedStates({});
      }
    }
  }, [filteredQuantumProducts]);

  /**
   *
   * @param alternate
   * @param tab
   */
  function onClickHandler(alternate: any, tab: string) {
    const vendor = vendorsList?.find(
      (vendor) => vendor.domain?.replace('www.', '')?.toLowerCase() === alternate?.vendor?.domain?.toLowerCase()
    );

    if (vendor) {
      setViewInfo?.({
        showNPIInfo: false,
        showToggle: true,
        showPartnerInfo: false
      });
    } else {
      setViewInfo?.({
        showNPIInfo: false,
        showToggle: false,
        showPartnerInfo: true
      });
    }
    setSelectedVendor({
      id: Number(vendor?.id),
      name: alternate.name,
      domain: alternate?.vendor?.domain
    });
    setSelectedSupplierCardTab && setSelectedSupplierCardTab(tab);
  }

  return (
    <Skeleton loading={loadingProductsAndAlternatives} active>
      <Row gutter={24}>
        <Col span={24}>
          <Row gutter={16} className="mb-6">
            <Col span={6}>
              <Input
                placeholder="Search Product"
                size="large"
                suffix={<SearchOutlined />}
                className="w-full"
                onChange={handleQuantumProductSearch}
                value={searchText}
                allowClear
              />
            </Col>
            <Col span={6}>
              <Select
                placeholder="Filter by category"
                size="large"
                className="w-full"
                options={quantumCategoryOptions}
                onChange={handleCategoryChange}
                allowClear
              />
            </Col>
          </Row>
          <List
            itemLayout="vertical"
            size="large"
            dataSource={filteredQuantumProducts}
            renderItem={(item: any, index) => (
              <div className="border border-solid border-gray-300 rounded-xl mb-6">
                <Row align="stretch" className="h-full">
                  {/* Left Section */}
                  <Col span={14}>
                    <List.Item key={item?.product?.id}>
                      <List.Item.Meta
                        title={<b className="text-lg">{item?.product?.name}</b>}
                        description={
                          item?.product?.description ? (
                            <p className={`text-base text-black ${expandedIndices.has(index) ? '' : 'line-clamp-3'}`}>
                              {item?.product?.description}
                            </p>
                          ) : (
                            <p className="text-zinc-500">
                              <ExclamationCircleFilled className="mt-1" /> <i>No description available</i>
                            </p>
                          )
                        }
                        className="product-alignment"
                      />
                      {expandedIndices.has(index) && (
                        <Row gutter={[0, 8]} className="mb-4">
                          <Col span={24}>
                            <h3 className="text-base font-semibold">Features</h3>
                          </Col>
                          <Col span={24}>
                            <Row gutter={[8, 8]}>
                              {item?.product?.features?.length > 0 ? (
                                item?.product?.features.map((feature: string, i: number) => (
                                  <Col key={i}>
                                    <span className="feature-item text-xs text-gray-700 bg-gray-200 px-2 py-1 rounded">
                                      {feature}
                                    </span>
                                  </Col>
                                ))
                              ) : (
                                <Col span={24}>
                                  <span className="text-xs text-gray-500">
                                    No features are available for this product.
                                  </span>
                                </Col>
                              )}
                            </Row>
                          </Col>
                        </Row>
                      )}
                      <Row justify="space-between" align="middle">
                        <Col>
                          <p className="text-xs text-gray-500 mt-1">
                            Category: {item?.product?.category || 'No category'}
                          </p>
                        </Col>
                        <Col>
                          <a
                            className="text-blue-500 text-xs font-semibold cursor-pointer"
                            onClick={() => toggleExpand(index)}
                          >
                            {expandedIndices.has(index) ? (
                              <>
                                <UpOutlined className="mr-1" />
                                Show Less Details
                              </>
                            ) : (
                              <>
                                <DownOutlined className="mr-1" />
                                Show More Details
                              </>
                            )}
                          </a>
                        </Col>
                      </Row>
                    </List.Item>
                  </Col>
                  <Col span={10} className="col-with-border-radius">
                    <SupplierProductAlternatives
                      product={item}
                      onClickHandler={onClickHandler}
                      setShowCompareProductsModal={setShowCompareProductsModal}
                      setCheckedStates={setCheckedStates}
                      checkedStates={checkedStates}
                      showMoreDetails={expandedIndices.has(index)}
                    />
                  </Col>
                </Row>
              </div>
            )}
          />
        </Col>
      </Row>
      <CompareProductsTableModal
        open={showCompareProductsModal?.open}
        title="Compare Products"
        okText=""
        cancelText="Close"
        cancelButtonProps={{
          type: 'primary',
          icon: <CloseOutlined />
        }}
        onCancel={() =>
          setShowCompareProductsModal({
            open: false,
            comparisonProducts: undefined,
            unionOfFeatures: undefined
          })
        }
        productsToCompare={{
          comparisonProducts: showCompareProductsModal?.comparisonProducts,
          unionOfFeatures: showCompareProductsModal.unionOfFeatures
        }}
      />
    </Skeleton>
  );
};
